import { useQuery } from '@tanstack/react-query';
import { PremisesServiceQueryKeys } from './config';
import { PremisesService } from './service';
import { usePremisesStore } from '../../store/usePremisesStore';
import { AREA_FILTER_MAX, AREA_FILTER_MIN } from 'features/premises/constants';
import { getCurrentSiteId } from 'features/services/utils/env-helper';
import { PremisesApiFilter } from 'features/premises/types';
import { apiErrorFrom } from 'features/errors';

/**
 * Hook for fetching premises with current filter state
 * Automatically refetches when filters change
 */
export const usePremisesQuery = (enabled: boolean = true) => {
    const tags = usePremisesStore(state => state.tags);
    const types = usePremisesStore(state => state.types);
    const minMaxArea = usePremisesStore(state => state.minMaxArea);
    const sortBy = usePremisesStore(state => state.sortBy);

    // Build filters object
    const filters: PremisesApiFilter = {
        siteId: [getCurrentSiteId()],
    };

    if (minMaxArea.min !== AREA_FILTER_MIN) {
        filters.minArea = minMaxArea.min;
    }

    if (minMaxArea.max !== AREA_FILTER_MAX) {
        filters.maxArea = minMaxArea.max;
    }

    if (tags.length > 0) {
        filters.tags = tags.map(x => x.id);
    }

    if (types.length > 0) {
        filters.type = types;
    }

    return useQuery({
        queryKey: PremisesServiceQueryKeys.listFiltered({ ...filters, sortBy }),
        queryFn: async () => {
            const response = await PremisesService.fetchPremises(filters, sortBy);
            if (response.error) throw apiErrorFrom(response);
            return response.data!.hits;
        },
        enabled,
        staleTime: 30 * 1000, // 30 seconds
    });
};

/**
 * Hook for fetching premises tags for the current site
 */
export const usePremisesTagsQuery = (enabled: boolean = true) => {
    const siteId = getCurrentSiteId();

    return useQuery({
        queryKey: PremisesServiceQueryKeys.tagsBySite(siteId),
        queryFn: async () => {
            const response = await PremisesService.fetchPremisesTags(siteId);
            if (response.error) throw apiErrorFrom(response);
            return response.data!;
        },
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes - tags don't change often
    });
};
